/**
 * Client-side resilience for DICOMweb frame loading.
 *
 * Two independent pieces, each behind its own config flag:
 *
 *   dicomwebRetry — retries a DICOMweb GET that fails at the network level
 *     (dropped/stale connection). cornerstone's streamRequest has no retry, so
 *     one dropped socket today means a permanently blank slice. Worth keeping.
 *
 *   suppressFrameFetchErrors — TEMPORARY. Drops cornerstone's unconditional
 *     `console.error(err)` for the same class of failure. Hides the log, not
 *     the cause. Remove after the presentation.
 *
 * Counters live on window.__dicomwebResilience so nothing is truly invisible.
 */

const NETWORK_FAILURE =
  /^(failed to fetch|load failed|networkerror when attempting to fetch resource\.?)$/i;

const DICOMWEB_PATH = '/dicomweb/';

function isNetworkFailure(err) {
  return err instanceof TypeError && NETWORK_FAILURE.test(err.message || '');
}

function isRetriableDicomwebRequest(input, init) {
  const method = (init?.method || (typeof input !== 'string' && input?.method) || 'GET').toUpperCase();
  if (method !== 'GET') {
    return false;
  }
  const url = typeof input === 'string' ? input : input?.url || '';
  return url.includes(DICOMWEB_PATH);
}

const stats = { retried: 0, recovered: 0, exhausted: 0, suppressed: 0 };

function installRetry(config) {
  const attempts = Number(config?.attempts) > 0 ? Number(config.attempts) : 2;
  const backoffMs = Number(config?.backoffMs) > 0 ? Number(config.backoffMs) : 150;
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    if (!isRetriableDicomwebRequest(input, init)) {
      return originalFetch(input, init);
    }

    let lastError;
    for (let attempt = 0; attempt <= attempts; attempt++) {
      try {
        const response = await originalFetch(input, init);
        if (attempt > 0) {
          stats.recovered += 1;
        }
        return response;
      } catch (err) {
        lastError = err;
        const aborted = err?.name === 'AbortError' || init?.signal?.aborted;
        if (aborted || !isNetworkFailure(err) || attempt === attempts) {
          break;
        }
        stats.retried += 1;
        const delay = backoffMs * Math.pow(2, attempt) * (0.5 + Math.random());
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    if (isNetworkFailure(lastError)) {
      stats.exhausted += 1;
    }
    throw lastError;
  };
}

function installErrorSuppression() {
  const originalError = console.error.bind(console);
  console.error = (...args) => {
    if (args.length === 1 && isNetworkFailure(args[0])) {
      stats.suppressed += 1;
      return;
    }
    originalError(...args);
  };
}

export default function installDicomwebResilience() {
  if (window.__dicomwebResilience) {
    return;
  }
  const retryConfig = window.config?.dicomwebRetry;
  const suppress = window.config?.suppressFrameFetchErrors;
  if (!retryConfig && !suppress) {
    return;
  }

  window.__dicomwebResilience = stats;

  if (retryConfig) {
    installRetry(retryConfig === true ? {} : retryConfig);
  }
  if (suppress) {
    installErrorSuppression();
    console.warn(
      '[medicaprof lab] DICOMweb frame fetch errors are suppressed. ' +
        'Counts: window.__dicomwebResilience'
    );
  }
}
